function wrap<T>(objToProxy: Meta<T>): MetaRoot<T> {
  return new Proxy<any>(objToProxy, {
    get: (target, prop) => {
      if (target[prop] === undefined) {
        target[prop] = wrap(new MetaImpl(objToProxy, prop as string))
      }
      return target[prop]
    },
  })
}

export type MetaRoot<T> = Required<
  {
    [P in keyof T]: MetaPath<NonNullable<T[P]>>
  }
>

export type MetaPath<T> = T extends ReadonlyArray<any>
  ? MetaArray<T[0]>
  : T extends object
  ? MetaRoot<T> & Meta<T>
  : Meta<T>

export interface Meta<T> {
  alias: string | null
  readonly type: T
  absolutePath(): string
}

export interface MetaArray<T> extends Meta<T[]> {
  get(index: number): MetaPath<T>
}

export class MetaImpl<T> implements Meta<T> {
  readonly type: T = null as T
  readonly alias: string | null
  private readonly parent: Meta<any> | null
  private readonly arrayItem: boolean

  constructor(parent: Meta<any> | null = null, alias: string | null = null, arrayItem: boolean = false) {
    this.parent = parent
    this.alias = alias
    this.arrayItem = arrayItem
  }

  public absolutePath = (): string => {
    let path: string | null = null
    if (this.parent?.absolutePath) {
      path = this.parent.absolutePath()
    }

    if (path && !this.arrayItem) {
      path += '.'
    } else if (!path) {
      path = ''
    }

    if (this.alias) {
      path += this.alias
    }

    return path
  }

  public get = (index: number): any => {
    return wrap(new MetaImpl<T>(this, `[${index}]`, true))
  }
}

export default function metaPath<T extends object>(): MetaRoot<T> {
  return wrap(new MetaImpl<T>())
}
